import React, { useState, useEffect } from "react";
import { Button, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { CartItem } from "interfaces/ICart";
import { useCartStore } from "../../../../zustand/cartStore";
import TrashIcon from "components/icons/TrashIcon";
import ImageModal from "components/general/ImageModal";
import { useBreakpoints } from "hooks/useBreakPoints";
import CustomToast from "components/general/CustomToast";
import "./ReviewItemsSection.scss";

interface ReviewItemsSectionProps {
  items: CartItem[];
  isActive: boolean;
  onPlaceOrder: () => void;
  disabled: boolean;
  onItemsConfirmed?: () => void;
  forceExpand?: boolean;
}

const ReviewItemsSection: React.FC<ReviewItemsSectionProps> = ({
  items,
  isActive,
  onPlaceOrder,
  disabled,
  onItemsConfirmed,
  forceExpand = false
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { updateQuantity, removeItem } = useCartStore();
  const { isMobile } = useBreakpoints();
  const [showToast, setShowToast] = useState<{
    message: string;
    show: boolean;
    success: boolean;
  }>({ message: "", show: false, success: true });

  // Expandir automáticamente cuando se recibe forceExpand
  useEffect(() => {
    if (forceExpand && isActive) {
      setIsExpanded(true);
    }
  }, [forceExpand, isActive]);

  // Get all images from all items
  const allImages = items.map(item => item.image);

  const handleToggleExpand = (e: React.MouseEvent) => {
    // Prevent expansion if clicking on an image or button
    if (
      (e.target as HTMLElement).closest('.item-image-container') ||
      (e.target as HTMLElement).closest('.preview-image-container') ||
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('.quantity-control')
    ) {
      return;
    }
    
    if (isActive) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleQuantityChange = (productId: number, colorIndex: number, sizeId: number, newQuantity: number, sizeProductId: number) => {
    console.log('Updating quantity:', { productId, colorIndex, sizeId, newQuantity, sizeProductId });
    const result = updateQuantity(productId, colorIndex, sizeId, sizeProductId, newQuantity);
    
    if (!result.success) {
      setShowToast({
        message: result.message || "Error al actualizar cantidad",
        show: true,
        success: false,
      });
    }
  };

  const handleRemoveItem = (productId: number, colorIndex: number, sizeId: number, sizeProductId: number) => {
    removeItem(productId, colorIndex, sizeId, sizeProductId);
  };

  const handleConfirm = () => {
    setIsConfirmed(true);
    setIsExpanded(false); // Automatically minimize the section
    
    // Scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    if (onItemsConfirmed) {
      onItemsConfirmed();
    }
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
  };

  const handleSelectImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  return (
    <div 
      className={`review-section ${isActive ? 'active' : 'inactive'} ${disabled ? 'disabled' : ''} ${!isExpanded ? 'minimized' : ''}`}
      onClick={handleToggleExpand}
    >
      <div className="section-header">
        <h3>Revisar artículos</h3>
      </div>
      
      {/* Horizontal scrollable images list - only visible when minimized */}
      {!isExpanded && (
        <div className="horizontal-items-preview">
          <div className="items-scroll-container">
            {items.map((item, index) => (
              <div key={`preview-${item.productId}-${item.colorIndex}-${item.sizeId}`} className="preview-item">
                <div 
                  className="preview-image-container"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick(index);
                  }}
                >
                  <img src={item.image} alt={item.name} className="preview-image" />
                </div>
                <div className="preview-item-name">{item.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {isActive && isExpanded && (
        <div className="review-content">
          <div className="review-items-container">
            {items.map((item, index) => (
              <div key={`${item.productId}-${item.colorIndex}-${item.sizeId}`} className="review-item">
                <Row className="align-items-center">
                  <Col xs={3} md={2}>
                    <div 
                      className="item-image-container"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(index);
                      }}
                    >
                      <img src={item.image} alt={item.name} className="item-image" />
                    </div>
                  </Col>
                  <Col xs={6} md={7}>
                    <div className="item-details">
                      <h5 className="item-name">{item.name}</h5>
                      <p className="item-color"><span className="detail-label">Color:</span> {item.colorName}</p>
                      <p className="item-size"><span className="detail-label">Talla:</span> {item.sizeName}</p>
                      <div className="quantity-control">
                        <span className="detail-label">Cantidad:</span>
                        <button
                          className="quantity-btn-minimal"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuantityChange(
                              item.productId,
                              item.colorIndex,
                              item.sizeId,
                              Math.max(1, item.quantity - 1),
                              item.sizeProductId
                            );
                          }}
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                        <span className="quantity-number">
                          {item.quantity}
                        </span>
                        <button
                          className={`quantity-btn-minimal ${
                            item.quantity >= item.stock ? "disabled" : ""
                          } ${item.quantity >= item.stock ? "custom-tooltip" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuantityChange(
                              item.productId,
                              item.colorIndex,
                              item.sizeId,
                              item.quantity + 1,
                              item.sizeProductId
                            );
                          }}

                          data-bs-toggle={item.quantity >= item.stock ? "tooltip" : ""}
                          data-bs-placement="top"
                          data-bs-title={item.quantity >= item.stock ? `No hay más stock disponible. Solo quedan ${item.stock} unidades.` : ""}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>
                    </div>
                  </Col>
                  <Col xs={3} md={3}>
                    <div className="item-price-container">
                      <button
                        className="remove-item-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveItem(
                            item.productId,
                            item.colorIndex,
                            item.sizeId,
                            item.sizeProductId
                          );
                        }}
                      >
                        <TrashIcon size={isMobile ? 18 : 22} color="#614900" />
                      </button>
                                              <div className="item-price">
                          {item.discount && item.discount > 0 ? (
                            <div className="price-with-discount">
                              <span className="discount-badge">
                                -{item.discountType?.includes('%') ? 
                                  `${item.discount}%` : 
                                  `$${item.discount}`
                                }
                              </span>
                              <span className="original-price">
                                ${(item.originalPrice * item.quantity).toLocaleString('es-MX', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              <div className="price-row">
                                <span className="detail-label">Precio:</span>
                                <span className="discounted-price">
                                  ${(item.finalPrice * item.quantity).toLocaleString('es-MX', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="price-row">
                              <span className="detail-label">Precio:</span>
                              <span className="price-value">
                                ${(item.price * item.quantity).toLocaleString('es-MX', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                    </div>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
          
          <div className="review-actions">
            <Button 
              variant="primary" 
              className="confirm-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleConfirm();
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        images={allImages}
        selectedIndex={selectedImageIndex}
        onSelectImage={handleSelectImage}
        isReadOnly={true}
      />

      {/* CustomToast component */}
      <CustomToast
        show={showToast.show}
        onClose={() =>
          setShowToast({
            message: "",
            show: false,
            success: true,
          })
        }
        message={showToast.message}
        width="300px"
        success={showToast.success}
      />
    </div>
  );
};

export default ReviewItemsSection;
